/**
 * 认证服务
 * 处理用户登录、登出、令牌刷新等认证操作
 */

import apiClient, { ApiError } from './apiClient';
import { API_ENDPOINTS } from '../config/api';
import studentLinkService from './studentLinkService';

/**
 * 认证服务
 */
const authService = {
  /**
   * 用户登录
   * @param {string} username - 用户名/邮箱/手机号
   * @param {string} password - 密码
   * @param {boolean} rememberMe - 是否记住登录状态
   * @returns {Promise<Object>} 登录结果
   */
  async login(username, password, rememberMe = false) {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, {
      username,
      password,
      remember_me: rememberMe,
    });

    // 保存认证信息
    if (response.access_token) {
      apiClient.setTokens(response.access_token, response.refresh_token);
    }
    if (response.user) {
      apiClient.setUser(response.user);
    }

    return response;
  },

  /**
   * 通过访问令牌登录
   * @param {string} token - 访问令牌
   * @returns {Promise<Object>} 登录结果
   */
  async tokenLogin(token) {
    const response = await apiClient.post(`/api/auth/token-login/${token}`);

    // 保存认证信息
    if (response.access_token) {
      apiClient.setTokens(response.access_token, response.refresh_token);
    }
    if (response.user) {
      apiClient.setUser(response.user);
    }

    return response;
  },

  /**
   * 用户登出
   * @returns {Promise<Object>}
   */
  async logout() {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      // 即使服务端登出失败，也清除本地认证信息
      console.warn('Server logout failed:', error.message);
    } finally {
      apiClient.clearAuth();
    }
    return { success: true };
  },

  /**
   * 获取当前用户信息
   * @returns {Promise<Object>}
   */
  async getCurrentUser() {
    const response = await apiClient.get(API_ENDPOINTS.AUTH.ME);
    if (response.user || response) {
      apiClient.setUser(response.user || response);
    }
    return response.user || response;
 },

  /**
   * 刷新访问令牌
   * @returns {Promise<Object>}
   */
  async refreshToken() {
    const refreshToken = apiClient.getRefreshToken();
    if (!refreshToken) {
      throw new ApiError('No refresh token available', 401);
    }

    const response = await apiClient.post(API_ENDPOINTS.AUTH.REFRESH, {
      refresh_token: refreshToken,
    });

    if (response.access_token) {
      apiClient.setTokens(response.access_token, response.refresh_token);
    }

    return response;
  },

  /**
   * 用户注册
   * @param {Object} userData - 用户注册数据
   * @returns {Promise<Object>}
   */
  async register(userData) {
    return apiClient.post(API_ENDPOINTS.AUTH.REGISTER, userData);
  },

  /**
   * 修改密码
   * @param {string} oldPassword - 原密码
   * @param {string} newPassword - 新密码
   * @returns {Promise<Object>}
   */
  async changePassword(oldPassword, newPassword) {
    return apiClient.post(API_ENDPOINTS.AUTH.ME_CHANGE_PASSWORD, {
      current_password: oldPassword,
      new_password: newPassword,
    });
  },

  /**
   * 检查是否已认证
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!apiClient.getAccessToken();
  },

  /**
   * 获取本地存储的用户信息
   * @returns {Object|null}
   */
  getStoredUser() {
    return apiClient.getUser();
  },

  /**
   * 获取用户权限列表
   * @returns {string[]}
   */
  getPermissions() {
    const user = this.getStoredUser();
    return user?.permissions || [];
  },

  /**
   * 获取用户角色
   * @returns {string|null}
   */
  getRole() {
    const user = this.getStoredUser();
    return user?.role || null;
  },

  /**
   * 获取当前学生用户关联的学生信息
   * @returns {Promise<Object>} 学生关联信息
   */
 async getCurrentStudentInfo() {
    try {
      // 如果用户角色是学生，则获取关联的学生信息
      const role = this.getRole();
      if (role === 'student') {
        const studentLink = await studentLinkService.getCurrentStudentLink();
        return studentLink;
      }
      return null;
    } catch (error) {
      console.error('获取学生关联信息失败:', error);
      throw error;
    }
  },

  /**
   * 获取当前学生用户关联的学生ID
   * @returns {Promise<number>} 学生ID
   */
  async getCurrentStudentId() {
    try {
      const role = this.getRole();
      if (role === 'student') {
        const studentLink = await studentLinkService.getCurrentStudentLink();
        if (studentLink && studentLink.student_id) {
          return studentLink.student_id;
        }
      }
      return null;
    } catch (error) {
      console.error('获取学生ID失败:', error);
      throw error;
    }
  },

  /**
   * 获取当前用户关联的所有学生信息
   * @returns {Promise<Array>} 学生信息数组
   */
  async getCurrentUserStudents() {
    try {
      const role = this.getRole();
      if (role === 'student') {
        const students = await studentLinkService.getCurrentUserStudents();
        return students;
      }
      return [];
    } catch (error) {
      console.error('获取用户关联学生失败:', error);
      throw error;
    }
  },
};

export default authService;
